import { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Icon,
  MOODS,
  MoodChip,
  MoodDot,
  OwlMark,
  Pill,
  cn,
  moodById,
  type Mood,
  type MoodId,
} from "@wiseowl/design-system";
import { api } from "../lib/api.js";
import { isApiError, type InsightDto } from "@wiseowl/shared";
import {
  getName as getStoredName,
  localDayKey,
  saveReflection,
} from "../lib/anon-reflection.js";
import { isEmbedded } from "../lib/embed.js";

type Mode = "authed" | "anon";
type Presentation = "overlay" | "inline";
type StepId = "breathe" | "mood" | "name" | "write" | "insight";

interface ReflectFlowProps {
  /** "authed" persists to the API; "anon" keeps it on the device. */
  mode?: Mode;
  /** Overlay = full-screen modal (authed); inline = embedded (landing). */
  presentation?: Presentation;
  /** Name to personalise the result; anon reads it from the device. */
  personaName?: string | null;
  initialMood?: MoodId | null;
  /** Overlay close / dismiss. */
  onClose?: () => void;
  /** Authed close-out, or anon "maybe later" → collapse to result view. */
  onComplete?: () => void;
  /** Anon-only: the sign-up-to-save CTA. */
  onSignUpToSave?: () => void;
  /** Anon-only: "Already have an account? Sign in" on the result screen. */
  onSignIn?: () => void;
}

/** Client-side calm fallback when the insight call can't be reached. */
const FALLBACK_INSIGHT: InsightDto = {
  acknowledgement: "You showed up and named something true. That counts.",
  toneTags: ["presence"],
  insight:
    "The owl is taking a slow breath and couldn't reply just now — but your words are safe here. Sometimes the noticing itself is the whole practice.",
  questions: ["What would the smallest kindness to yourself look like right now?"],
  tinyAction: "When you close this, then take one slow breath before the next thing.",
  promptVersion: "client-fallback@v1",
  modelId: "fallback/offline",
};

// Soothing entrance motion for steps + the mood discs. Honours
// prefers-reduced-motion (no transform/opacity churn for those users).
const STEP_KEYFRAMES = `
@keyframes wo-step-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes wo-pop { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
.wo-step-in { animation: wo-step-in 0.34s cubic-bezier(0.22, 0.61, 0.36, 1) both; }
@media (prefers-reduced-motion: reduce) {
  .wo-step-in { animation: none; }
  .wo-pop { animation: none !important; }
}`;

const stepsFor = (mode: Mode, needName: boolean): StepId[] =>
  mode === "anon"
    ? needName
      ? ["mood", "breathe", "name", "write", "insight"]
      : ["mood", "breathe", "write", "insight"]
    : ["breathe", "mood", "write", "insight"];

/**
 * ReflectFlow — the guided reflection (Breathe · Mood · Reflect · Insight).
 *
 * One component, two modes. Authed (overlay) persists to the API and ends
 * with the daily-reset close-out. Anon (inline, on the landing) generates
 * the insight via the public endpoint, keeps everything on the device,
 * captures a name once, and ends with a sign-up-to-save CTA.
 */
export const ReflectFlow = ({
  mode = "authed",
  presentation = "overlay",
  personaName,
  initialMood,
  onClose,
  onComplete,
  onSignUpToSave,
  onSignIn,
}: ReflectFlowProps) => {
  const [name, setNameState] = useState<string>(personaName ?? "");
  const needName = mode === "anon" && !(personaName ?? getStoredName());
  const steps = stepsFor(mode, needName);

  const [idx, setIdx] = useState(0);
  const [mood, setMood] = useState<MoodId>(initialMood ?? "stressed");
  const [text, setText] = useState("");
  const [insight, setInsight] = useState<InsightDto | null>(null);
  const [reflectionId, setReflectionId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [insightError, setInsightError] = useState<string | null>(null);

  const total = steps.length;
  const current = steps[idx]!;
  const next = () => setIdx((i) => Math.min(i + 1, total - 1));
  const back = () => (idx === 0 ? onClose?.() : setIdx((i) => i - 1));
  const currentMood = moodById(mood) ?? MOODS[1]!;
  const displayName = (name || personaName || getStoredName() || "").trim();

  // Persist the anon reflection to the device once we have an insight.
  const persistAnon = (ins: InsightDto) =>
    saveReflection({
      date: localDayKey(),
      moodSlug: mood,
      name: displayName || null,
      text,
      insight: ins,
      createdAt: new Date().toISOString(),
    });

  // Write → Insight. Anon: public endpoint + device save. Authed: API.
  const submitReflection = async () => {
    if (submitting) return;
    setSubmitting(true);
    setInsightError(null);

    if (mode === "anon") {
      next(); // show the insight step with its listening skeleton
      const r = await api<{ insight: InsightDto }>("/public/insight", {
        method: "POST",
        body: JSON.stringify({ moodSlug: mood, text }),
      });
      setSubmitting(false);
      if (isApiError(r)) {
        setInsightError(r.error.message);
        return;
      }
      setInsight(r.data.insight);
      persistAnon(r.data.insight);
      return;
    }

    // Authed: create the reflection, then fetch its insight.
    const r = await api<{ reflection: { id: string } }>("/reflections", {
      method: "POST",
      body: JSON.stringify({ practiceSlug: "daily-reset", moodSlug: mood, body: text }),
    });
    if (isApiError(r)) {
      setSubmitting(false);
      setInsightError(r.error.message);
      return;
    }
    const id = r.data.reflection.id;
    setReflectionId(id);
    next();
    const ins = await api<{ insight: InsightDto }>(`/reflections/${id}/insight`);
    setSubmitting(false);
    if (isApiError(ins)) {
      setInsightError(ins.error.message);
      return;
    }
    setInsight(ins.data.insight);
  };

  // Insight error → "Save my words" keeps the reflection with the calm
  // offline fallback so nothing written is lost.
  const saveWords = () => {
    persistAnon(FALLBACK_INSIGHT);
    onComplete?.();
  };

  const onAuthedDone = async () => {
    if (reflectionId) {
      await api("/practices/daily-reset/complete", {
        method: "POST",
        body: JSON.stringify({ reflectionId, moodAfter: mood }),
      }).catch(() => undefined);
    }
    onComplete?.();
  };

  const body = (
    <>
      <header
        className={cn(
          "z-10 flex items-center justify-between",
          presentation === "overlay" ? "sticky top-0 bg-cream p-5" : "pb-4",
        )}
      >
        {presentation === "overlay" ? (
          <Button variant="ghost" size="sm" onClick={back} aria-label="Back" className="!min-w-0 !px-3">
            <Icon name="back" size={18} />
          </Button>
        ) : (
          <button
            type="button"
            onClick={back}
            aria-label="Back"
            disabled={idx === 0}
            className="text-muted hover:text-ink disabled:opacity-0"
          >
            <Icon name="back" size={18} />
          </button>
        )}
        <div className="flex gap-1" aria-hidden="true">
          {steps.map((_, i) => (
            <span
              key={i}
              className={cn(
                "h-1 w-7 rounded-sm transition-colors duration-500 ease-soft",
                i <= idx ? "bg-teal" : "bg-line-strong",
              )}
            />
          ))}
        </div>
        {presentation === "overlay" ? (
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close" className="!min-w-0 !px-3">
            <Icon name="close" size={18} />
          </Button>
        ) : (
          <span className="w-[18px]" />
        )}
      </header>

      <div
        className={cn(
          "mx-auto w-full max-w-[640px]",
          presentation === "overlay" ? "flex-1 px-5 pb-10 pt-3 md:px-8" : "px-1",
        )}
      >
        {/* Each step fades + rises in on entry (keyed so it re-triggers). */}
        <div key={current} className="wo-step-in">
          {current === "breathe" && <StepBreathe onNext={next} />}
          {current === "mood" && <StepMood mood={mood} setMood={setMood} onNext={next} />}
          {current === "name" && (
            <StepName name={name} setName={setNameState} onNext={next} onSkip={next} />
          )}
          {current === "write" && (
            <StepWrite text={text} setText={setText} mood={currentMood} onNext={submitReflection} submitting={submitting} anon={mode === "anon"} />
          )}
          {current === "insight" && (
            <StepInsight
              insight={insight}
              error={insightError}
              mode={mode}
              name={displayName}
              onRetry={() => { setIdx(steps.indexOf("write")); }}
              onSaveWords={saveWords}
              onComplete={mode === "anon" ? onComplete : onAuthedDone}
              onSignUpToSave={onSignUpToSave}
              onSignIn={onSignIn}
            />
          )}
        </div>
      </div>
      <style>{STEP_KEYFRAMES}</style>
    </>
  );

  if (presentation === "inline") {
    return (
      <Card className={`!p-5 md:!p-7 ${isEmbedded() ? "shadow-2" : ""}`} surface="paper">
        {body}
      </Card>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Today's reset"
      className="fixed inset-0 z-50 flex flex-col overflow-auto bg-cream text-ink"
    >
      {body}
    </div>
  );
};

const StepBreathe = ({ onNext }: { onNext: () => void }) => {
  // Guided inhale: count 4 → 0, then invite one slow breath before continuing.
  const [count, setCount] = useState(4);
  const done = count === 0;

  useEffect(() => {
    if (count === 0) return;
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count]);

  return (
    <div className="pt-2 text-center">
      <div className="mb-7 font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
        Arrive
      </div>
      <div className="relative inline-block">
        <div
          className="grid h-[200px] w-[200px] animate-breathe place-items-center rounded-pill border border-line"
          style={{
            background: "radial-gradient(circle, rgb(var(--wo-cream-2)) 0%, rgb(var(--wo-cream)) 60%)",
            boxShadow: "inset 0 0 60px rgb(var(--wo-teal) / 0.08)",
          }}
        >
          <div className="grid h-[120px] w-[120px] place-items-center rounded-pill bg-teal text-cream">
            <div>
              <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-cream/60">
                {done ? "And now" : "Breathe in"}
              </div>
              <div className="mt-1 font-serif text-[48px] leading-none">{count}</div>
            </div>
          </div>
        </div>
      </div>
      <p className="mt-8 px-5 font-serif text-h2 leading-snug text-ink">
        {done ? "Take one deep breath, and let it go slowly." : "Inhale slowly. Feel the chair beneath you."}
      </p>
      <p className="mt-2 text-small text-muted">
        {done ? "When you’re ready, we’ll begin." : "Follow the count down to zero."}
      </p>
      <Button
        size="lg"
        onClick={onNext}
        disabled={!done}
        className="mt-9 w-full sm:w-auto sm:min-w-[220px]"
        trailingIcon={<Icon name="arrow" size={16} />}
      >
        I&apos;m here
      </Button>
    </div>
  );
};

// Opening question (kept as a list so more can be added later).
const MOOD_QUESTIONS = [
  "How are you feeling today?",
];

const StepMood = ({
  mood,
  setMood,
  onNext,
}: {
  mood: MoodId;
  setMood: (m: MoodId) => void;
  onNext: () => void;
}) => {
  const [question] = useState(
    () => MOOD_QUESTIONS[Math.floor(Math.random() * MOOD_QUESTIONS.length)]!,
  );
  return (
    <div>
      <div className="mb-3 font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
        Mood
      </div>
      <h2 className="font-serif text-h1 font-medium leading-snug tracking-tight text-ink text-pretty">
        {question}
      </h2>
      <p className="mt-1.5 text-muted">One word is enough.</p>
      <div className="mt-6 grid grid-cols-4 gap-2.5 sm:gap-3.5">
        {MOODS.map((m, i) => (
          <div
            key={m.id}
            className="wo-pop flex justify-center"
            style={{ animation: `wo-pop 0.32s ${i * 0.035}s cubic-bezier(0.34, 1.56, 0.64, 1) both` }}
          >
            <MoodDot mood={m} active={mood === m.id} onClick={() => setMood(m.id)} size={52} />
          </div>
        ))}
      </div>
      <Button size="lg" fullWidth onClick={onNext} className="mt-9" trailingIcon={<Icon name="arrow" size={16} />}>
        Continue
      </Button>
    </div>
  );
};

const StepName = ({
  name,
  setName,
  onNext,
  onSkip,
}: {
  name: string;
  setName: (s: string) => void;
  onNext: () => void;
  onSkip: () => void;
}) => (
  <div>
    <div className="mb-3 font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
      Before we go on
    </div>
    <h2 className="font-serif text-h1 font-medium leading-snug tracking-tight text-ink text-pretty">
      What may we call you?
    </h2>
    <p className="mt-1.5 text-muted">Just a first name, so we can speak to you. You can skip this.</p>
    <input
      value={name}
      onChange={(e) => setName(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) onNext(); }}
      placeholder="Your name"
      autoFocus={!isEmbedded()}
      className="mt-5 w-full rounded-md border border-line bg-paper p-4 font-serif text-body text-ink outline-none focus-visible:ring-2 focus-visible:ring-mustard"
    />
    <div className="mt-5 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center">
      <Button
        size="lg"
        onClick={() => (name.trim() ? onNext() : onSkip())}
        fullWidth
        className="sm:w-auto sm:min-w-[160px]"
        trailingIcon={<Icon name="arrow" size={16} />}
      >
        Continue
      </Button>
      <button type="button" onClick={onSkip} className="text-small text-muted hover:text-ink sm:px-2">
        Skip
      </button>
    </div>
  </div>
);

const StepWrite = ({
  text,
  setText,
  mood,
  onNext,
  submitting,
  anon,
}: {
  text: string;
  setText: (s: string) => void;
  mood: Mood;
  onNext: () => void;
  submitting: boolean;
  anon: boolean;
}) => (
  <div>
    <div className="mb-3 font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
      Reflect
    </div>
    <div className="mb-2 flex items-center gap-2">
      <MoodChip mood={mood} />
      <span className="text-small text-muted">
        For you, feeling <strong className="text-ink">{mood.name.toLowerCase()}</strong>:
      </span>
    </div>
    <h2 className="font-serif text-h2 font-medium leading-snug tracking-tight text-ink text-pretty">
      What feels heaviest about today?
    </h2>
    <textarea
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder="Write freely — or tap the mic and speak. Nobody is reading over your shoulder."
      className="mt-4 min-h-[160px] w-full resize-y rounded-md border border-line bg-paper p-4 font-serif text-body leading-relaxed text-ink outline-none focus-visible:ring-2 focus-visible:ring-mustard"
    />
    <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-2.5">
        {micSupported() && (
          <MicButton onText={(t) => setText(text.trim() ? `${text.trim()} ${t}` : t)} />
        )}
        <span className="truncate text-meta text-muted">
          {text.length} characters · {anon ? "lives on your device" : "private"}
        </span>
      </div>
      <Button
        size="lg"
        fullWidth
        className="sm:w-auto"
        onClick={onNext}
        disabled={submitting || text.trim().length < 5}
        trailingIcon={<Icon name="arrow" size={16} />}
      >
        {submitting ? "Sending…" : "Reflect now"}
      </Button>
    </div>
  </div>
);

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

const micSupported = (): boolean =>
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== "undefined";

/** POST a recorded clip to the Cloudflare-AI Whisper endpoint (English). */
const transcribeAudio = async (blob: Blob): Promise<string> => {
  try {
    const res = await fetch(`${API_BASE}/api/public/transcribe`, {
      method: "POST",
      headers: { "Content-Type": blob.type || "audio/webm" },
      body: blob,
    });
    if (!res.ok) return "";
    const json = (await res.json()) as { data?: { text?: string } };
    return (json.data?.text ?? "").trim();
  } catch {
    return "";
  }
};

/** Tap to record, tap to stop → transcribed text appended to the reflection. */
const MicButton = ({ onText }: { onText: (t: string) => void }) => {
  const [phase, setPhase] = useState<"idle" | "recording" | "working">("idle");
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Stop the recorder if the step unmounts mid-recording.
  useEffect(() => () => {
    if (recRef.current?.state === "recording") recRef.current.stop();
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setPhase("working");
        const t = await transcribeAudio(blob);
        if (t) onText(t);
        setPhase("idle");
      };
      recRef.current = rec;
      rec.start();
      setPhase("recording");
    } catch {
      setPhase("idle"); // permission denied or no device
    }
  };

  const label = phase === "recording" ? "Stop" : phase === "working" ? "Transcribing…" : "Speak";
  return (
    <button
      type="button"
      onClick={phase === "recording" ? () => recRef.current?.stop() : phase === "idle" ? start : undefined}
      disabled={phase === "working"}
      aria-label={label}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-pill border px-3 py-1.5 text-meta font-semibold transition-colors",
        phase === "recording"
          ? "border-mustard bg-mustard-soft text-ink"
          : "border-line bg-paper text-ink-soft hover:bg-cream-2",
        phase === "working" && "opacity-70",
      )}
    >
      <Icon name="mic" size={14} className={phase === "recording" ? "text-mustard" : "text-muted"} />
      {label}
      {phase === "recording" && <span className="h-1.5 w-1.5 animate-pulse rounded-pill bg-mustard" />}
    </button>
  );
};

const StepInsight = ({
  insight,
  error,
  mode,
  name,
  onRetry,
  onSaveWords,
  onComplete,
  onSignUpToSave,
  onSignIn,
}: {
  insight: InsightDto | null;
  error: string | null;
  mode: Mode;
  name: string;
  onRetry: () => void;
  onSaveWords: () => void;
  onComplete?: () => void;
  onSignUpToSave?: () => void;
  onSignIn?: () => void;
}) => {
  if (error) {
    return (
      <div className="space-y-4">
        <p className="font-serif text-h3 italic text-ink-soft">The owl is taking a slow breath.</p>
        <p className="text-small text-muted">{error}</p>
        <div className="flex flex-col gap-2.5 sm:flex-row">
          <Button variant="mustard" fullWidth onClick={onRetry}>Try again</Button>
          {mode === "anon" && (
            <Button variant="ghost" fullWidth onClick={onSaveWords}>Save my words</Button>
          )}
        </div>
      </div>
    );
  }
  if (!insight) {
    return (
      <div className="space-y-3.5">
        <div className="flex items-center gap-2.5">
          <OwlMark size={32} />
          <div className="font-serif text-small italic text-ink-soft">Listening…</div>
        </div>
        <div className="h-3 w-2/3 animate-pulse rounded bg-line" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-line" />
        <div className="h-24 w-full animate-pulse rounded-md bg-line/60" />
      </div>
    );
  }
  return (
    <div>
      <div className="mb-3.5 flex items-center gap-2.5">
        <OwlMark size={32} />
        <div className="font-serif text-small italic text-ink-soft">
          {name ? `${name}, we noticed —` : "We noticed —"}
        </div>
      </div>

      {insight.toneTags.length > 0 && (
        <div className="mb-3.5 flex flex-wrap gap-1.5">
          {insight.toneTags.map((t) => (
            <Pill key={t} className="bg-m-stressed/20 text-ink">{t}</Pill>
          ))}
        </div>
      )}

      <p className="font-serif text-h2 font-medium leading-snug tracking-tight text-ink text-pretty">
        &ldquo;{insight.acknowledgement}&rdquo;
      </p>

      <Card surface="cream-2" className="mt-5">
        <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-teal">
          A gentle insight
        </div>
        <p className="mt-2 font-serif text-h3 leading-relaxed text-ink">{insight.insight}</p>
      </Card>

      {insight.questions.length > 0 && (
        <div className="mt-5">
          <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-mustard">
            Questions to sit with
          </div>
          <div className="mt-2.5 flex flex-col gap-2.5">
            {insight.questions.map((q, i) => (
              <div key={i} className="flex gap-2.5">
                <span className="font-serif text-h2 italic leading-none text-mustard">·</span>
                <span className="font-serif text-body italic leading-snug text-ink-soft">{q}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Card surface="ink" className="mt-5">
        <div className="flex items-center gap-1.5 font-mono text-eyebrow font-semibold uppercase tracking-wider text-mustard">
          <Icon name="leaf" size={16} /> One tiny action · today
        </div>
        <p className="mt-2.5 font-serif text-h3 leading-relaxed text-cream">{insight.tinyAction}</p>
      </Card>

      {mode === "anon" ? (
        <div className="mt-6">
          <Card surface="cream-2" className="!bg-mustard-soft">
            <p className="text-small text-ink">
              This reflection lives only on your device. Sign up to save it and build a personal mood bank.
            </p>
          </Card>
          <div className="mt-3 flex flex-col gap-2.5 sm:flex-row">
            <Button variant="mustard" fullWidth onClick={onSignUpToSave}>Sign up to save</Button>
            <Button variant="ghost" fullWidth onClick={onComplete}>Maybe later</Button>
          </div>
          {onSignIn && (
            <p className="mt-3 text-center text-small text-muted">
              Already have an account?{" "}
              <button type="button" onClick={onSignIn} className="font-medium text-teal hover:underline">Sign in</button>
            </p>
          )}
        </div>
      ) : (
        <div className="mt-6 flex gap-2.5">
          <Button variant="ghost" fullWidth onClick={onComplete}>I&apos;m done</Button>
          <Button variant="mustard" fullWidth onClick={onComplete}>Save reflection</Button>
        </div>
      )}
    </div>
  );
};
